import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { EMERGENCY_CONTACTS_LIST, EMERGENCY_CONTACT_SUBMIT, EMERGENCY_CONTACT_DELETE } from '../../api/constants';

// Fetch Emergency Contacts
export const fetchEmergencyContacts = createAsyncThunk(
  'emergencyContact/fetchContacts',
  async ({ page = 1, limit = 10, student_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('page', page);
      formData.append('limit', limit);
      if (student_id) {
        formData.append('student_id', student_id);
      }

      const response = await apiHandler(EMERGENCY_CONTACTS_LIST, 'POST', formData);
      return response.data.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch emergency contacts');
    }
  }
);

// Submit Emergency Contact (Create/Edit)
export const submitEmergencyContact = createAsyncThunk(
  'emergencyContact/submitContact',
  async (contactData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.keys(contactData).forEach(key => {
        if (contactData[key] !== null && contactData[key] !== undefined) {
          formData.append(key, contactData[key]);
        }
      });
      
      const response = await apiHandler(EMERGENCY_CONTACT_SUBMIT, 'POST', formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit emergency contact');
    }
  }
);

// Delete Emergency Contact
export const deleteEmergencyContact = createAsyncThunk(
  'emergencyContact/deleteContact',
  async (contactId, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('id', contactId);
      
      const response = await apiHandler(EMERGENCY_CONTACT_DELETE, 'POST', formData);
      return { id: contactId, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete emergency contact');
    }
  }
);

const emergencyContactSlice = createSlice({
  name: 'emergencyContact',
  initialState: {
    contacts: [],
    meta: null,
    loading: false,
    // See the matching comment in childRegistrationSlice.js's initialState —
    // same "have we actually asked the server yet" flag, for the same
    // reason: an empty `contacts` array is ambiguous between "not fetched
    // yet" and "fetched, genuinely none on file" for anything (the header's
    // Registration Packet banner) that treats an empty list as "incomplete".
    initialized: false,
    error: null,
    submitLoading: false,
    submitError: null,
    deleteLoading: false,
    deleteError: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Contacts
      .addCase(fetchEmergencyContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmergencyContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.contacts = Array.isArray(action.payload?.data) ? action.payload.data : [];
        state.meta = action.payload?.meta || null;
      })
      .addCase(fetchEmergencyContacts.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.payload;
      })
      // Submit Contact
      .addCase(submitEmergencyContact.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitEmergencyContact.fulfilled, (state, action) => {
        state.submitLoading = false;
        const newContact = action.payload;
        
        // If updating existing contact
        if (newContact?.id) {
          const index = state.contacts.findIndex(c => c.id === newContact.id);
          if (index !== -1) {
            state.contacts[index] = newContact;
          } else {
            state.contacts.unshift(newContact);
          }
        } else if (newContact) {
          state.contacts.unshift(newContact);
        }
      })
      .addCase(submitEmergencyContact.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload;
      })
      // Delete Contact
      .addCase(deleteEmergencyContact.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteEmergencyContact.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.contacts = state.contacts.filter(c => c.id !== action.payload.id);
      })
      .addCase(deleteEmergencyContact.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      });
  }
});

// Selectors
export const selectContacts = (state) => state.emergencyContact.contacts;
export const selectMeta = (state) => state.emergencyContact.meta;
export const selectLoading = (state) => state.emergencyContact.loading;
export const selectInitialized = (state) => state.emergencyContact.initialized;
export const selectError = (state) => state.emergencyContact.error;
export const selectSubmitLoading = (state) => state.emergencyContact.submitLoading;
export const selectSubmitError = (state) => state.emergencyContact.submitError;
export const selectDeleteLoading = (state) => state.emergencyContact.deleteLoading;
export const selectDeleteError = (state) => state.emergencyContact.deleteError;
export const selectContactById = (state, contactId) => {
  return state.emergencyContact.contacts.find(contact => contact.id === parseInt(contactId));
};

export default emergencyContactSlice.reducer;
